/**
 * Feedback - Session feedback from insured persons (Versicherte)
 * Accessible to all admin roles (org-scoped for administrators)
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { MessageSquareText, Star, ShieldAlert } from "lucide-react";
import type { FunctionReturnType } from "convex/server";
import type { api as apiType } from "@jourfx/convex/client";
import { PageHeader } from "../components/PageHeader";

type FeedbackItem = FunctionReturnType<typeof apiType.admin.feedback.list>["items"][number];
import { useDateFormat } from "../hooks/useDateFormat";
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@jourfx/ui";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`size-4 ${
            star <= rating
              ? "fill-amber-400 text-amber-400"
              : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
}

function StatsCards({
  stats,
  t,
}: {
  stats: { total: number; averageRating: number; distribution: Record<number, number> };
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("stats.totalFeedback")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("stats.averageRating")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold">{stats.averageRating}</span>
            <StarRating rating={Math.round(stats.averageRating)} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("stats.ratingDistribution")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1.5 h-10">
            {[1, 2, 3, 4, 5].map((star) => {
              const count = stats.distribution[star] || 0;
              const maxCount = Math.max(...Object.values(stats.distribution), 1);
              const height = (count / maxCount) * 100;
              return (
                <div key={star} className="flex flex-1 flex-col items-center gap-0.5">
                  <div
                    className="w-full rounded-sm bg-amber-400/80"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${star}★: ${count}`}
                  />
                  <span className="text-[10px] text-muted-foreground">{star}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FeedbackContent() {
  const { t } = useTranslation("feedback");
  const { t: tc } = useTranslation("common");
  const { formatShortDate, formatShortTime } = useDateFormat();

  const [ratingFilter, setRatingFilter] = useState<number | undefined>(undefined);
  const [cursor, setCursor] = useState<number>(0);

  const stats = useQuery(api.admin.feedback.stats);
  const feedbackResult = useQuery(api.admin.feedback.list, {
    ratingFilter,
    limit: 50,
    cursor,
  });

  const formatTimestamp = (timestamp: number) =>
    `${formatShortDate(timestamp)} ${formatShortTime(timestamp)}`;

  const formatDuration = (seconds: number) => {
    const minutes = Math.round(seconds / 60);
    return t("table.minutes", { count: minutes });
  };

  if (feedbackResult === undefined || stats === undefined) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (feedbackResult === null || stats === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc("auth.loginRequired")}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc("auth.loginRequiredMessage")}
        </p>
      </div>
    );
  }

  const items = feedbackResult.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
      />

      <StatsCards stats={stats} t={t} />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={ratingFilter ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            setRatingFilter(val ? Number(val) : undefined);
            setCursor(0);
          }}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="">{t("filters.allRatings")}</option>
          {[5, 4, 3, 2, 1].map((r) => (
            <option key={r} value={r}>
              {r === 1 ? t("filters.star", { count: r }) : t("filters.stars", { count: r })}
            </option>
          ))}
        </select>
        {ratingFilter !== undefined && (
          <Button
            variant="outline"
            onClick={() => {
              setRatingFilter(undefined);
              setCursor(0);
            }}
          >
            {t("filters.reset")}
          </Button>
        )}
      </div>

      {/* Table or empty state */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-card p-12 text-center">
          <MessageSquareText className="size-12 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium text-foreground">{t("empty.title")}</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-sm">
            {t("empty.description")}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border bg-card">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t("table.date")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t("table.versicherter")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t("table.examiner")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t("table.rating")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t("table.comment")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t("table.duration")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item: FeedbackItem) => (
                  <tr key={item._id} className="hover:bg-muted/50">
                    <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(item.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      {item.versichertenName || t("table.unknown")}
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      {item.gutachterName || t("table.unknown")}
                    </td>
                    <td className="px-6 py-4">
                      <StarRating rating={item.rating} />
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground max-w-xs truncate">
                      {item.comment || (
                        <span className="text-muted-foreground italic">
                          {t("table.noComment")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDuration(item.sessionDuration)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t("pagination.entries", { count: feedbackResult.total })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCursor(Math.max(0, cursor - 50))}
                disabled={cursor === 0}
              >
                {t("pagination.previous")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (feedbackResult.nextCursor != null) {
                    setCursor(feedbackResult.nextCursor);
                  }
                }}
                disabled={feedbackResult.nextCursor == null}
              >
                {t("pagination.next")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function Feedback() {
  return <FeedbackContent />;
}
